import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Tv, Radio, Play, Layers, Sparkles, Monitor, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const onAirVideos = [
  {
    id: 'khqW24El1Ls',
    title: 'SCCN Broadcast ID',
    thumbnail: 'https://img.youtube.com/vi/khqW24El1Ls/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/khqW24El1Ls'
  },
  {
    id: '7pGE0UrSCGw',
    title: 'SDTV',
    thumbnail: 'https://img.youtube.com/vi/7pGE0UrSCGw/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/7pGE0UrSCGw'
  },
  {
    id: 'Ufexp0wrbmg',
    title: 'ATV Broadcast ID',
    thumbnail: 'https://img.youtube.com/vi/Ufexp0wrbmg/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/Ufexp0wrbmg'
  },
  {
    id: 'sOOpOfySDEI',
    title: 'BarronTV - Channel ID',
    thumbnail: 'https://img.youtube.com/vi/sOOpOfySDEI/hqdefault.jpg',
    embedUrl: 'https://www.youtube.com/embed/sOOpOfySDEI'
  },
  {
    id: 'qhbd_88YCt0',
    title: 'Op eigen kracht',
    thumbnail: 'https://customer-assets.emergentagent.com/job_2c966088-8797-43ff-a34e-a4775ed9024e/artifacts/s3xwiiwp_op-eigen-kracht.jpg',
    embedUrl: 'https://www.youtube.com/embed/qhbd_88YCt0'
  }
];

const sdtvImages = [
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/d3wxllgc_1-SDTV---Channel-ID.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/xfevhd6y_2-SDTV---Channel-ID.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/1ie46dzn_3-SDTV---Channel-ID.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/de62u75o_4-SDTV---Channel-ID.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/y5jf2wm0_05-ATV-Programmaoverzicht.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/vl8k2ajs_01-ATV-Programmering.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/lywkfgap_02-ATV-Overlay.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/hwrnappy_BarronTV%20Programmaoverzicht.00_01_04_21.Still001.jpg',
  'https://customer-assets.emergentagent.com/job_visual-studio-104/artifacts/10t3yvz0_94GREENTV-Program-Identity.jpg',
  'https://customer-assets.emergentagent.com/job_2c966088-8797-43ff-a34e-a4775ed9024e/artifacts/vz6pqh27_94news.jpg'
];

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const OnAirIdentityPage = () => {
  const [currentVideo, setCurrentVideo] = useState(onAirVideos[0]);
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const [videoKey, setVideoKey] = useState(0);
  const [currentImageSlide, setCurrentImageSlide] = useState(0);
  const [shuffledImages, setShuffledImages] = useState(() => shuffleArray(sdtvImages));
  const thumbnailsPerPage = 4;
  const { t } = useLanguage();

  // Image slider auto-advance
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageSlide((prev) => (prev + 1) % shuffledImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [shuffledImages.length]);

  const nextImageSlide = () => {
    setCurrentImageSlide((prev) => (prev + 1) % shuffledImages.length);
  };

  const prevImageSlide = () => {
    setCurrentImageSlide((prev) => (prev - 1 + shuffledImages.length) % shuffledImages.length);
  };

  const visibleThumbnails = onAirVideos.slice(
    thumbnailStartIndex,
    thumbnailStartIndex + thumbnailsPerPage
  );

  const canGoNext = thumbnailStartIndex + thumbnailsPerPage < onAirVideos.length;
  const canGoPrev = thumbnailStartIndex > 0;

  const nextThumbnails = () => {
    if (canGoNext) {
      setThumbnailStartIndex(thumbnailStartIndex + thumbnailsPerPage);
    }
  };

  const prevThumbnails = () => {
    if (canGoPrev) {
      setThumbnailStartIndex(thumbnailStartIndex - thumbnailsPerPage);
    }
  };

  const selectVideo = (video) => {
    setCurrentVideo(video);
    setVideoKey(prev => prev + 1);
  };

  // Build embed URL with autoplay parameter - mute=1 required for browser autoplay policy
  const getEmbedUrl = () => {
    return `${currentVideo.embedUrl}?autoplay=1&mute=1&rel=0&enablejsapi=1&playsinline=1`;
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Image Slider Section */}
      <section className="relative w-full h-auto md:h-[70vh] bg-black overflow-hidden">
        {/* Background Images */}
        {shuffledImages.map((image, index) => (
          <div
            key={index}
            className={`${index === currentImageSlide ? 'relative' : 'absolute inset-0'} transition-opacity duration-700 ${
              index === currentImageSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <img
              src={image}
              alt={`On-Air Identity ${index + 1}`}
              className="w-full h-auto md:h-[70vh] object-contain md:object-cover mx-auto"
            />
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevImageSlide}
          className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white bg-black/30 flex items-center justify-center transition-all duration-300 hover:bg-white/30"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
        </button>

        <button
          onClick={nextImageSlide}
          className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-white bg-black/30 flex items-center justify-center transition-all duration-300 hover:bg-white/30"
          aria-label="Next image"
        >
          <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
          {shuffledImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentImageSlide ? 'bg-white w-6' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>
      
      {/* Page Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-montserrat-bold text-3xl md:text-4xl text-[#0b004c] mb-4">
              {t('onAirIdentityTitle')}
            </h1>
            <p className="font-montserrat text-[#6a6a6a] max-w-2xl mx-auto mb-8">
              {t('onAirIdentityDesc1')}
            </p>
            
            {/* Video Player */}
            <div className="max-w-4xl mx-auto mb-8">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  key={videoKey}
                  className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
                  src={getEmbedUrl()}
                  title={currentVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>

            {/* Thumbnail Navigation */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="flex items-center gap-4">
                {/* Previous Arrow */}
                <button
                  onClick={prevThumbnails}
                  disabled={!canGoPrev}
                  className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    canGoPrev
                      ? 'border-[#0b004c] text-[#0b004c] hover:bg-[#0b004c] hover:text-white cursor-pointer'
                      : 'border-gray-300 text-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Previous thumbnails"
                >
                  <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                </button>

                {/* Thumbnails */}
                <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  {visibleThumbnails.map((video) => (
                    <div key={video.id} className="flex flex-col">
                      <button
                        onClick={() => selectVideo(video)}
                        className={`relative aspect-video rounded-lg overflow-hidden group transition-all duration-300 ${
                          currentVideo.id === video.id
                            ? 'ring-2 ring-[#0b004c] ring-offset-2'
                            : 'hover:ring-2 hover:ring-[#6a6a6a] hover:ring-offset-2'
                        }`}
                      >
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;
                          }}
                        />
                        {/* Play Overlay */}
                        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                          currentVideo.id === video.id
                            ? 'bg-[#0b004c]/50'
                            : 'bg-black/30 group-hover:bg-black/50'
                        }`}>
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                            currentVideo.id === video.id
                              ? 'bg-white text-[#0b004c]'
                              : 'bg-white/90 text-[#0b004c]'
                          }`}>
                            <Play className="w-5 h-5 md:w-6 md:h-6 ml-1" fill="currentColor" />
                          </div>
                        </div>
                      </button>
                      {/* Video Title */}
                      <p className={`mt-2 text-xs md:text-sm font-montserrat text-center truncate ${
                        currentVideo.id === video.id
                          ? 'text-[#0b004c] font-montserrat-bold'
                          : 'text-[#6a6a6a]'
                      }`}>
                        {video.title}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Next Arrow */}
                <button
                  onClick={nextThumbnails}
                  disabled={!canGoNext}
                  className={`flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    canGoNext
                      ? 'border-[#0b004c] text-[#0b004c] hover:bg-[#0b004c] hover:text-white cursor-pointer'
                      : 'border-gray-300 text-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Next thumbnails"
                >
                  <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>

              {/* Page Indicator */}
              <div className="flex justify-center mt-4 gap-2">
                {Array.from({ length: Math.ceil(onAirVideos.length / thumbnailsPerPage) }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setThumbnailStartIndex(index * thumbnailsPerPage)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      Math.floor(thumbnailStartIndex / thumbnailsPerPage) === index
                        ? 'bg-[#0b004c] w-6'
                        : 'bg-[#6a6a6a]/30 hover:bg-[#6a6a6a]/50'
                    }`}
                    aria-label={`Go to page ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Tv, titleKey: 'channelBrandingTitle', descKey: 'channelBrandingDesc', features: ['logoDesign', 'brandGuidelines', 'colorPalette', 'typographySystem'] },
              { icon: Play, titleKey: 'identsBumpersTitle', descKey: 'identsBumpersDesc', features: ['stationIdents', 'programBumpers', 'sponsorTags', 'breakStings'] },
              { icon: Layers, titleKey: 'lowerThirdsTitle', descKey: 'lowerThirdsDesc', features: ['lowerThirds', 'newsTickers', 'scoreGraphics', 'infoPanels'] },
              { icon: Monitor, titleKey: 'showPackagingTitle', descKey: 'showPackagingDesc', features: ['showOpeners', 'transitions', 'endCredits', 'segmentHeaders'] },
              { icon: Sparkles, titleKey: 'motionGraphicsTitle', descKey: 'motionGraphicsDesc', features: ['animatedLogos', 'titleSequences', 'visualEffects', 'threeDElements'] },
              { icon: Radio, titleKey: 'promoProductionTitle', descKey: 'promoProductionDesc', features: ['showPromos', 'eventTeasers', 'comingUpNext', 'specialAnnouncements'] }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 group"
                >
                  <div className="w-12 h-12 bg-[#0b004c] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#0b004c]/80 transition-colors duration-300">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-montserrat-bold text-xl text-[#0b004c] mb-2">
                    {t(service.titleKey)}
                  </h3>
                  <p className="font-montserrat text-[#6a6a6a] text-sm mb-4">
                    {t(service.descKey)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {service.features.map((feature, i) => (
                      <span
                        key={i}
                        className="text-xs font-montserrat bg-[#0b004c]/10 text-[#0b004c] px-2 py-1 rounded"
                      >
                        {t(feature)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="font-montserrat-bold text-2xl md:text-3xl text-[#0b004c] text-center mb-12">
            {t('onAirProcess')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", titleKey: "discovery", descKey: "discoveryDesc" },
              { step: "02", titleKey: "concept", descKey: "conceptDesc" },
              { step: "03", titleKey: "design", descKey: "designDesc" },
              { step: "04", titleKey: "deliveryProcess", descKey: "deliveryProcessDesc" }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 bg-[#0b004c] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-montserrat-bold text-xl">{item.step}</span>
                </div>
                <h3 className="font-montserrat-bold text-lg text-[#0b004c] mb-2">{t(item.titleKey)}</h3>
                <p className="font-montserrat text-[#6a6a6a] text-sm">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#0b004c] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-montserrat-bold text-2xl md:text-3xl text-white mb-4">
            {t('readyElevateBroadcast')}
          </h2>
          <p className="font-montserrat text-white/80 mb-8 max-w-xl mx-auto">
            {t('onAirCTADesc')}
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-[#0b004c] font-montserrat-bold px-8 py-3 rounded hover:bg-[#6a6a6a] hover:text-white transition-colors duration-300"
          >
            {t('getStarted')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnAirIdentityPage;
